import {
  masterUserStates,
  COMPONENTS_WITH_LOBS_MAP,
} from "constants-lib/userState"

/**
 * Checks if the given cqType is a Lob State Component.
 *
 * @param {string} cqType - The cqType to check.
 * @returns {boolean} - Returns true if the cqType is a Lob State Component, otherwise false.
 */
export const isLobStateComponent = (cqType) =>
  COMPONENTS_WITH_LOBS_MAP[cqType] === true

/**
 * Filters the given user states based on the journey states.
 *  Get the Journey states the author added to this component.
 * @param {Array<string>} userStates - The array of user states.
 * @returns {Array<string>} - The filtered array of user states.
 */
export const getJourneyStates = (userStates) =>
  userStates.filter(
    (key) => masterUserStates.types.journey.statesList.indexOf(key) !== -1,
  )

/**
 * Filters the given array of user states and returns only the states that are present in the list of lob states.
 * Get the LOB states the author added to this component.
 * @param {Array<string>} userStates - The array of user states to filter.
 * @returns {Array<string>} - The filtered array of user states.
 */
const getLobStates = (userStates) =>
  userStates.filter(
    (key) => masterUserStates.types.lob.statesList.indexOf(key) !== -1,
  )

/**
 * Get the states to filter with.
 * Retrieves the filter states from the given author state object.
 * @param {Object} authorState - The author state object.
 * @returns {Object} - An object containing the filter journey states and filter lob states.
 */
const getFilterStates = (authorState) => {
  const authorTypes = Object.keys(authorState)
  const filterJourneyStates = []
  const filterLobStates = []

  authorTypes.forEach((authorTypeKey) => {
    const authorStates = Object.keys(authorState[authorTypeKey])
    authorStates.forEach((authorStateKey) => {
      if (authorState[authorTypeKey][authorStateKey] === true) {
        if (authorTypeKey === `journey`) {
          filterJourneyStates.push(authorStateKey)
        } else if (authorTypeKey === `lob`) {
          filterLobStates.push(authorStateKey)
        }
      }
    })
  })

  return {
    filterJourneyStates,
    filterLobStates,
  }
}

/**
 * Checks if the user state matches the author state.
 * @param {object} authorState - The author's state.
 * @param {array} userStates - The user's states.
 * @param {string} cqType - The component's cqType.
 * @returns {boolean} - True if the user state matches the author state, false otherwise.
 */
export const isMatchedUserState = (authorState, userStates, cqType) => {
  const { filterJourneyStates, filterLobStates } = getFilterStates(authorState)

  /* Get the component's Journey states. */
  const componentJourneyStates = getJourneyStates(userStates)

  /* Get the component's LOB states. */
  const componentLobStates = getLobStates(userStates)

  /* If the author has selected any user states to filter for. */
  const isFilterEnabled =
    filterJourneyStates.length > 0 || filterLobStates.length > 0

  /* If nothing is selected in the user state filter, show the component. */
  if (!isFilterEnabled) {
    return true
  }

  /* Check if any LOB states are selected to filter for. */
  // const hasLobState = filterLobStates.length > 0

  /*
    If component has any LOB states, require exact match to the filter.
    If no LOB state were selected to filter with, then only one journey
    state needs to match.
  */
  if (
    isLobStateComponent(cqType) ||
    componentLobStates.length > 0
    // && hasLobState
  ) {
    /* The states have to have the same length to match. */
    if (
      filterJourneyStates.length !== componentJourneyStates.length ||
      filterLobStates.length !== componentLobStates.length
    ) {
      return false
    }

    /* Check if journey states match. */
    let journeyMatches = true
    filterJourneyStates.forEach((s) => {
      journeyMatches =
        journeyMatches && componentJourneyStates.indexOf(s) !== -1
    })

    /* Check if LOB states match. */
    let lobMatches = true
    filterLobStates.forEach((s) => {
      lobMatches = lobMatches && componentLobStates.indexOf(s) !== -1
    })

    return journeyMatches && lobMatches
  }

  /*
    Only one journey state needs to match since no LOB states were selected for
    filtering.
  */
  return (
    filterJourneyStates.filter((s) => componentJourneyStates.indexOf(s) !== -1)
      .length > 0
  )
}
